import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Megaphone } from 'lucide-react';
import { motion } from 'framer-motion';
import { adsAPI } from '../lib/api';
import { getImageUrl } from '../lib/imageUrl';
import { adPlacementLabels } from '../lib/ads';
import type { AdCampaign, AdPlacement } from '../types';

interface AdUnitProps {
  placement: AdPlacement;
  category?: string;
  compact?: boolean;
  className?: string;
}

export function AdUnit({ placement, category, compact = false, className = '' }: AdUnitProps) {
  const [ads, setAds] = useState<AdCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const trackedAdId = useRef<number | null>(null);

  useEffect(() => {
    const loadAds = async () => {
      try {
        setLoading(true);
        const response = await adsAPI.getPlacement(placement, category, 1);
        setAds(response.data.ads || []);
      } catch (error) {
        setAds([]);
      } finally {
        setLoading(false);
      }
    };

    loadAds();
  }, [placement, category]);

  const currentAd = useMemo(() => ads[0], [ads]);

  useEffect(() => {
    if (!currentAd || trackedAdId.current === currentAd.id) {
      return;
    }

    trackedAdId.current = currentAd.id;
    adsAPI.recordImpression(currentAd.id).catch(() => undefined);
  }, [currentAd]);

  const handleClick = () => {
    if (!currentAd) return;
    adsAPI.recordClick(currentAd.id).catch(() => undefined);
  };

  if (loading) {
    return null;
  }

  if (!currentAd) {
    return (
      <div className={`rounded-2xl border border-dashed border-orange-300/70 dark:border-orange-500/30 bg-orange-50/60 dark:bg-orange-500/10 px-5 py-4 text-center text-sm font-medium text-orange-700 dark:text-orange-200 ${className}`}>
        Espaço disponível para publicidade
      </div>
    );
  }

  const imageSrc = getImageUrl(currentAd.image_url || undefined);

  return (
    <motion.section
      className={className}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="rounded-2xl overflow-hidden border border-orange-200/70 dark:border-orange-500/20 bg-white dark:bg-dark-800 shadow-lg shadow-orange-500/5">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-orange-100 dark:border-orange-500/10 bg-orange-50/70 dark:bg-orange-500/10">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500 text-white text-[10px] font-bold uppercase tracking-[0.14em] flex-shrink-0">
              <Megaphone size={12} />
              Publicidade
            </span>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">
              {currentAd.sponsor_name}
            </p>
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            {adPlacementLabels[placement]}
          </span>
        </div>

        <div className={`grid gap-0 ${compact ? 'md:grid-cols-[220px,1fr]' : 'md:grid-cols-[280px,1fr]'}`}>
          {currentAd.video_url ? (
            <div className={`relative ${compact ? 'h-44 md:h-full' : 'h-52 md:h-full'} bg-gray-100 dark:bg-dark-700`}>
              <video
                src={currentAd.video_url}
                controls
                preload="metadata"
                className="w-full h-full object-cover"
              />
            </div>
          ) : imageSrc && (
            <div className={`relative ${compact ? 'h-44 md:h-full' : 'h-52 md:h-full'} bg-gray-100 dark:bg-dark-700`}>
              <img
                src={imageSrc}
                alt={currentAd.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          )}

          <div className="p-5 md:p-6 flex flex-col justify-between min-w-0">
            <div>
              <h3 className="text-xl md:text-2xl font-serif font-bold text-gray-900 dark:text-white mb-2 leading-tight">
                {currentAd.title}
              </h3>
              {currentAd.description && (
                <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                  {currentAd.description}
                </p>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <a
                href={currentAd.target_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-orange text-white font-semibold hover:shadow-lg hover:shadow-orange-500/25 transition-all duration-300"
              >
                {currentAd.button_text || 'Saiba mais'}
                <ArrowRight size={16} />
              </a>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Patrocinado por {currentAd.sponsor_name}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
}